import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { can } from '../../lib/permissions';
import {
  AdminUserRow,
  AdminUsersPage,
  AdminUsersSort,
  AdminUsersTab,
  DeletedUserRow,
  getAdminUsers
} from '../../api/services/adminUsersService';
import UserDetailModal from '../../components/UserDetailModal';
import { Avatar as DgAvatar, Card, FilterChip, ICONS as DG_ICONS, LineIcon, LinkButton, SectionLabel, StatusPill } from '../../components/ui/dg';
import UserSidePanel, { fullName, ROLE, SOURCE, VERIFICATION, formatMoment, VerifiedBadge } from './users/UserSidePanel';

/**
 * Section Utilisateurs : chiffres du haut, onglets avec compteurs, recherche
 * et filtres, table paginée côté serveur, et un panneau latéral qui résume le
 * compte choisi (`?compte=<id>`). La fiche complète (UserDetailModal) reste
 * l'endroit où l'on agit : le panneau y mène, onglet ou action dépliés.
 */

const TABS: { id: AdminUsersTab; label: string; adminOnly?: boolean }[] = [
  { id: 'all', label: 'Tous' },
  { id: 'users', label: 'Utilisateurs' },
  { id: 'staff', label: 'Staff' },
  { id: 'pending', label: 'Permis à vérifier' },
  { id: 'suspended', label: 'Bloqués' },
  { id: 'deleted', label: 'Supprimés', adminOnly: true }
];

const SORTS: { id: AdminUsersSort; label: string }[] = [
  { id: 'recent', label: 'Plus récents' },
  { id: 'oldest', label: 'Plus anciens' },
  { id: 'name', label: 'Nom (A → Z)' },
  { id: 'last_login', label: 'Dernière connexion' },
  { id: 'trips', label: 'Plus de trajets' },
  { id: 'listings', label: 'Plus d’annonces' }
];


const isDeleted = (row: AdminUserRow | DeletedUserRow): row is DeletedUserRow => !('role' in row);

/** Pages à afficher : 1 … 4 5 6 … 16 */
const pageList = (page: number, pages: number): (number | '…')[] => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const sorted = Array.from(new Set([1, pages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pages))).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
};

const useOutsideClose = (open: boolean, close: () => void) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) close(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, close]);
  return ref;
};

export const Icon = ({ d, className = 'h-5 w-5' }: { d: string; className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>
);
const ICONS = {
  users: 'M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM21 19v-1a4 4 0 0 0-3-3.87M15.5 3.13a3.5 3.5 0 0 1 0 6.74',
  shield: 'M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3zM9 12l2 2 4-4',
  badge: 'M9 12l2 2 4-4M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  plus: 'M15 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM19 8v6M22 11h-6',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-3.5-3.5',
  filter: 'M4 5h16M7 12h10M10 19h4',
  columns: 'M4 5h16v14H4zM9.5 5v14M14.5 5v14',
  chevronLeft: 'M15 18l-6-6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14'
};

const Checkbox = ({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) => (
  <input type="checkbox" checked={checked} onChange={onChange} onClick={(e) => e.stopPropagation()} aria-label={label} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
);

type ModalState = { user: AdminUserRow; tab?: any; action?: 'notify' | 'suspend' | 'delete' | null };

const ManagementUsers = () => {
  const { token, user: me } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isAdmin = me?.role === 'admin';
  const canSuspend = can(me?.role, 'user.suspend');

  // Préremplie par la recherche globale de la sidebar (?q=...)
  const [search, setSearch] = useState(() => params.get('q') || '');
  const [query, setQuery] = useState(() => (params.get('q') || '').trim());
  const [tab, setTab] = useState<AdminUsersTab>(() => (TABS.some((t) => t.id === params.get('onglet')) ? params.get('onglet') as AdminUsersTab : 'all'));
  const [role, setRole] = useState('all');
  const [status, setStatus] = useState('all');
  const [source, setSource] = useState('all');
  const [verification, setVerification] = useState('all');
  const [sort, setSort] = useState<AdminUsersSort>('recent');
  const [page, setPage] = useState(1);
  const limit = 10;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [data, setData] = useState<AdminUsersPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const panelId = params.get('compte');
  const [panelVersion, setPanelVersion] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);

  const closeMenu = useCallback(() => setMenuFor(null), []);
  const menuRef = useOutsideClose(!!menuFor, closeMenu);

  // La recherche part 250 ms après la dernière frappe.
  useEffect(() => {
    const id = window.setTimeout(() => setQuery(search.trim()), 250);
    return () => window.clearTimeout(id);
  }, [search]);

  // Tout changement de critère ramène en page 1 et vide la sélection.
  useEffect(() => { setPage(1); setSelected(new Set()); }, [query, tab, role, status, source, verification, sort, limit]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getAdminUsers(token, { q: query, tab, role, status, source, verification, sort, page, limit }, controller.signal)
      .then((result) => setData(result))
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message || 'Utilisateurs indisponibles.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token, query, tab, role, status, source, verification, sort, page, limit, reloadKey]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key); else next.set(key, value);
    setParams(next, { replace: key !== 'compte' });
  };
  const openPanel = (id: string | null) => setParam('compte', id);
  const chooseTab = (id: AdminUsersTab) => { setTab(id); setParam('onglet', id === 'all' ? null : id); };

  const rows = data?.rows || [];
  const liveRows = rows.filter((r): r is AdminUserRow => !isDeleted(r));
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin);
  const filtersActive = [role, status, source, verification].filter((v) => v !== 'all').length;
  // perd 400 px et elles ne reviennent que sur très grand écran.
  const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? 's' : ''}`;
  const allOnPage = liveRows.length > 0 && liveRows.every((r) => selected.has(r.id_user));

  const togglePage = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allOnPage) liveRows.forEach((r) => next.delete(r.id_user)); else liveRows.forEach((r) => next.add(r.id_user));
    return next;
  });
  const toggleRow = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Export de la sélection (page courante) en CSV, pour un tableur.
  const exportSelection = () => {
    const chosen = liveRows.filter((r) => selected.has(r.id_user));
    if (!chosen.length) return;
    const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['Prénom', 'Nom', 'E-mail', 'Téléphone', 'Rôle', 'Statut', 'Vérification', 'Annonces', 'Trajets', 'Source', 'Inscription', 'Dernière connexion'];
    const lines = chosen.map((r) => [r.first_name, r.last_name, r.email, r.phone, ROLE[r.role]?.label || r.role, r.is_suspended ? 'Suspendu' : 'Actif', VERIFICATION[r.verification].label, r.listings, r.trips, SOURCE[r.auth_provider] || r.auth_provider, r.registration_date, r.last_login].map(cell).join(';'));
    const blob = new Blob(['﻿' + [head.map(cell).join(';'), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `utilisateurs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const seeTrips = (u: AdminUserRow) => navigate(`/mytrips?q=${encodeURIComponent(u.email || fullName(u))}`);

  const rowMenu = (u: AdminUserRow) => {
    const items: { label: string; run: () => void; danger?: boolean }[] = [
      { label: 'Ouvrir le résumé', run: () => openPanel(u.id_user) },
      { label: 'Fiche complète', run: () => setModal({ user: u }) },
      { label: 'Voir ses trajets', run: () => seeTrips(u) },
      { label: 'Envoyer une notification', run: () => setModal({ user: u, action: 'notify' }) }
    ];
    if (u.email) items.push({ label: 'Copier l’e-mail', run: () => { navigator.clipboard?.writeText(u.email || '').catch(() => {}); } });
    if (canSuspend && u.id_user !== me?.id_user) {
      items.push(u.is_suspended
        ? { label: 'Réactiver le compte…', run: () => setModal({ user: u }) }
        : { label: 'Suspendre…', run: () => setModal({ user: u, action: 'suspend' }), danger: true });
    }
    return (
      <div ref={menuRef} className="absolute right-2 top-full z-30 mt-1 w-56 rounded-xl border border-gray-100 bg-white p-1 text-left shadow-lg">
        {items.map((item) => (
          <button key={item.label} type="button" onClick={(e) => { e.stopPropagation(); setMenuFor(null); item.run(); }} className={`w-full rounded-lg px-3 py-2 text-left text-[13px] hover:bg-gray-50 ${item.danger ? 'text-red-600' : 'text-gray-700'}`}>
            {item.label}
          </button>
        ))}
      </div>
    );
  };

  // Littérales pour que Tailwind les trouve : en-tête et lignes partagent la grille.
  const COLS = 'grid-cols-[2.4fr_1fr_0.8fr_0.8fr_1fr_1.2fr_32px]';
  const COLS_MD = 'md:grid-cols-[2.4fr_1fr_0.8fr_0.8fr_1fr_1.2fr_32px]';
  const roleTone = (r: string) => (r === 'admin' || r === 'moderator' ? 'indigo' : 'grey') as 'indigo' | 'grey';
  const ratingOf = (u: AdminUserRow) => (Number(u.total_reviews) > 0 ? Number(u.average_rating).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : null);
  const stateOf = (u: AdminUserRow): { label: string; tone: 'green' | 'red' | 'amber' } =>
    u.is_suspended ? { label: 'Bloqué', tone: 'red' } : u.verification === 'pending' ? { label: 'Permis à vérifier', tone: 'amber' } : { label: 'Actif', tone: 'green' };
  const avatarTone = (u: AdminUserRow) => (u.role === 'admin' || u.role === 'moderator' ? 'indigo' : u.listings > 0 ? 'orange' : 'grey') as any;
  const photo = (url?: string | null) => (!url ? null : /^https?:\/\//.test(url) ? url : `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`);

  return (
    <main className="flex min-h-0 flex-1 gap-4 overflow-hidden bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
      <div className="flex min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto [&>*]:shrink-0">
        {/* Catégories, recherche, filtres */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {visibleTabs.map((t) => {
              const count = data?.counts?.[t.id];
              return (
                <FilterChip key={t.id} active={tab === t.id} onClick={() => chooseTab(t.id)}
                  count={count !== undefined && count !== null ? count.toLocaleString('fr-FR') : undefined}
                  countTone={t.id === 'suspended' || t.id === 'pending' ? 'amber' : undefined}>
                  {t.label}
                </FilterChip>
              );
            })}
          </div>
          <span className="flex-1" />
          <div className="relative w-full sm:w-[220px]">
            <LineIcon d={DG_ICONS.search} size={15} color="#9CA3AF" className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === 'deleted' ? 'Un e-mail…' : 'Nom, e-mail, téléphone…'} aria-label="Rechercher un utilisateur"
              className="h-8 w-full rounded-full border border-[#E5E7EB] bg-white pl-9 pr-3 text-[12.5px] focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10" />
          </div>
          {tab !== 'deleted' && (
            <FilterChip active={filtersOpen || filtersActive > 0} onClick={() => setFiltersOpen((v) => !v)} count={filtersActive || undefined}
              icon={<LineIcon d={DG_ICONS.filter} size={14} color={filtersOpen || filtersActive > 0 ? '#fff' : '#4B5563'} />}>
              Filtrer
            </FilterChip>
          )}
        </div>

        {filtersOpen && tab !== 'deleted' && (
          <Card className="flex flex-wrap items-end gap-4 px-5 py-4">
            {[
              { label: 'Rôle', value: role, set: setRole, options: [['all', 'Tous'], ...Object.entries(ROLE).map(([id, r]) => [id, r.label])] },
              { label: 'État', value: status, set: setStatus, options: [['all', 'Tous'], ['active', 'Actifs'], ['suspended', 'Bloqués']] },
              { label: 'Vérification', value: verification, set: setVerification, options: [['all', 'Toutes'], ...Object.entries(VERIFICATION).map(([id, v]) => [id, v.label])] },
              { label: 'Source', value: source, set: setSource, options: [['all', 'Toutes'], ...Object.entries(SOURCE)] },
              { label: 'Tri', value: sort, set: (v: string) => setSort(v as AdminUsersSort), options: SORTS.map((s) => [s.id, s.label]) },
            ].map((f) => (
              <label key={f.label} className="flex flex-col gap-1">
                <SectionLabel>{f.label}</SectionLabel>
                <select value={f.value} onChange={(e) => f.set(e.target.value)}
                  className="h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]">
                  {f.options.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </label>
            ))}
            {filtersActive > 0 && <LinkButton onClick={() => { setRole('all'); setStatus('all'); setSource('all'); setVerification('all'); }}>Tout effacer</LinkButton>}
          </Card>
        )}

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#EEEDFD] px-4 py-2.5 text-[13px] text-[#312E81]">
            <span className="font-bold">{plural(selected.size, 'sélectionné')}</span>
            <LinkButton onClick={exportSelection}>Exporter (CSV)</LinkButton>
            <span className="flex-1" />
            <LinkButton onClick={() => setSelected(new Set())}>Désélectionner</LinkButton>
          </div>
        )}

        <Card className="overflow-hidden">
          {error ? (
            <div className="px-6 py-12 text-center">
              <p className="text-[15px] font-bold text-[#1E293B]">Les comptes ne se chargent pas</p>
              <p className="mt-1 text-[13px] text-[#6B7280]">Le service ne répond pas pour l’instant.</p>
              <div className="mt-3"><LinkButton onClick={() => setReloadKey((k) => k + 1)}>Réessayer</LinkButton></div>
            </div>
          ) : tab === 'deleted' ? (
            <DeletedList rows={data?.tab === 'deleted' ? rows.filter(isDeleted) : []} loading={loading} />
          ) : (
            <>
              <div className={`hidden items-center gap-3 px-5 pb-2.5 pt-3 md:grid ${COLS}`}>
                <span className="flex items-center gap-3"><Checkbox checked={allOnPage} onChange={togglePage} label="Tout sélectionner sur la page" /><SectionLabel>Personne</SectionLabel></span>
                {['Rôle', 'Note', 'Locations', 'Inscrit le', 'État'].map((h) => <SectionLabel key={h}>{h}</SectionLabel>)}
                <span />
              </div>
              <div className={`transition-opacity ${loading && data ? 'opacity-60' : ''}`}>
                {loading && !data ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 border-t border-[#EEF0F4] px-5 py-3"><div className="h-9 w-9 animate-pulse rounded-full bg-[#EEF0F5]" /><div className="flex-1 space-y-2"><div className="h-3.5 w-44 animate-pulse rounded bg-[#EEF0F5]" /><div className="h-3 w-56 animate-pulse rounded bg-[#EEF0F5]" /></div></div>
                  ))
                ) : liveRows.length === 0 ? (
                  <p className="border-t border-[#EEF0F4] px-5 py-14 text-center text-[13px] text-[#6B7280]">Aucun compte ne correspond à ces critères.</p>
                ) : liveRows.map((u) => {
                  const active = panelId === u.id_user;
                  const st = stateOf(u);
                  const r = ratingOf(u);
                  return (
                    <div key={u.id_user} role="button" tabIndex={0}
                      onClick={() => openPanel(active ? null : u.id_user)}
                      onKeyDown={(e) => { if (e.key === 'Enter') openPanel(active ? null : u.id_user); }}
                      className={`relative grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 border-t border-[#EEF0F4] px-5 py-[11px] transition-colors ${COLS_MD} ${
                        active ? 'bg-[#F7F6FF] shadow-[inset_3px_0_0_#4F46E5]' : 'hover:bg-[#FBFBFE]'
                      }`}>
                      <span className="flex min-w-0 items-center gap-3">
                        <span className="hidden md:block"><Checkbox checked={selected.has(u.id_user)} onChange={() => toggleRow(u.id_user)} label={`Sélectionner ${fullName(u)}`} /></span>
                        <DgAvatar name={fullName(u)} photo={photo(u.profile_photo)} size={36} tone={avatarTone(u)} />
                        <span className="min-w-0">
                          <span className="flex min-w-0 items-center gap-1">
                            <span className="truncate text-[14px] font-semibold text-[#1E293B]">{fullName(u)}</span>
                            {u.verification === 'verified' && <VerifiedBadge />}
                          </span>
                          <span className="block truncate text-[12px] text-[#6B7280]">{u.email || u.phone || '—'}</span>
                        </span>
                      </span>
                      <span className="hidden md:block"><StatusPill tone={roleTone(u.role)}>{ROLE[u.role]?.label || u.role}</StatusPill></span>
                      <span className="hidden items-center gap-1 text-[13px] text-[#1E293B] md:flex">{r ? <><span className="text-[#F59E0B]">★</span>{r}</> : 'Nouveau'}</span>
                      <span className="hidden text-[13px] text-[#1E293B] md:block">{u.trips}</span>
                      <span className="hidden text-[12.5px] text-[#6B7280] md:block">{u.registration_date ? new Date(u.registration_date).toLocaleDateString('fr-FR') : '—'}</span>
                      <span><StatusPill tone={st.tone}>{st.label}</StatusPill></span>
                      <span className="relative hidden md:block">
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === u.id_user ? null : u.id_user); }}
                          onMouseDown={(e) => e.stopPropagation()}
                          aria-label={`Actions pour ${fullName(u)}`} aria-expanded={menuFor === u.id_user}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] hover:bg-[#F1F2F8] hover:text-[#1E293B]">
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
                        </button>
                        {menuFor === u.id_user && rowMenu(u)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Pied : combien, et la suite */}
          {!error && total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EEF0F4] px-5 py-3.5 text-[12.5px] text-[#6B7280]">
              <span className="tabular-nums">{from}–{to} sur {total.toLocaleString('fr-FR')}</span>
              {pages > 1 && (
                <nav className="flex items-center gap-1" aria-label="Pages">
                  <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Page précédente" className="flex h-8 w-8 items-center justify-center rounded-full text-[#4B5563] hover:bg-[#F1F2F8] disabled:opacity-40"><Icon d={ICONS.chevronLeft} className="h-4 w-4" /></button>
                  {pageList(page, pages).map((p, i) => (p === '…' ? (
                    <span key={`gap${i}`} className="px-1 text-[#9CA3AF]">…</span>
                  ) : (
                    <button key={p} type="button" onClick={() => setPage(p)} aria-current={p === page ? 'page' : undefined} className={`h-8 min-w-[2rem] rounded-full px-2 font-bold tabular-nums ${p === page ? 'bg-[#1E293B] text-white' : 'text-[#4B5563] hover:bg-[#F1F2F8]'}`}>{p}</button>
                  )))}
                  <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Page suivante" className="flex h-8 w-8 items-center justify-center rounded-full text-[#4B5563] hover:bg-[#F1F2F8] disabled:opacity-40"><Icon d={ICONS.chevronRight} className="h-4 w-4" /></button>
                </nav>
              )}
            </div>
          )}
        </Card>
      </div>

      {panelId && token && (
        <div className="xl:w-[380px] xl:shrink-0">
          <UserSidePanel
            key={`${panelId}:${panelVersion}`}
            userId={panelId}
            token={token}
            canSuspend={canSuspend && panelId !== me?.id_user}
            onClose={() => openPanel(null)}
            onOpenFull={(u, tabId, action) => setModal({ user: u, tab: tabId, action })}
            onSeeTrips={seeTrips}
          />
        </div>
      )}

      <UserDetailModal
        user={modal?.user as any}
        isOpen={!!modal}
        initialTab={modal?.tab}
        initialAction={modal?.action}
        onClose={() => setModal(null)}
        onUserUpdated={(updated) => {
          setData((prev) => (prev ? { ...prev, rows: prev.rows.map((r) => (!isDeleted(r) && r.id_user === updated.id_user ? { ...r, ...(updated as any) } : r)) } : prev));
          setPanelVersion((v) => v + 1);
          setReloadKey((k) => k + 1);
        }}
        onUserDeleted={(userId) => {
          setModal(null);
          if (panelId === userId) openPanel(null);
          setReloadKey((k) => k + 1);
        }}
      />
    </main>
  );
};

/** Onglet « Supprimés » : ce que le journal d'audit a gardé des comptes effacés. */
const DeletedList = ({ rows, loading }: { rows: DeletedUserRow[]; loading: boolean }) => {
  if (loading && rows.length === 0) return <div className="border-t border-gray-100 px-5 py-10"><div className="h-3.5 w-56 animate-pulse rounded bg-gray-100" /></div>;
  if (rows.length === 0) return <p className="border-t border-gray-100 px-5 py-14 text-center text-sm text-gray-500">Aucun compte supprimé depuis le back-office.</p>;
  return (
    <div className="border-t border-gray-100">
      <p className="bg-gray-50/70 px-4 py-2.5 text-[12px] text-gray-500 lg:px-5">La suppression est définitive : il ne reste que l’e-mail, la date et la personne qui l’a faite.</p>
      <ul className="divide-y divide-gray-100">
        {rows.map((r) => (
          <li key={`${r.id_user}${r.deleted_at}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-[13px] lg:px-5">
            <span className="min-w-0 flex-1 truncate font-medium text-gray-900">{r.email || 'Adresse inconnue'}</span>
            <span className="text-gray-500">Supprimé · {formatMoment(r.deleted_at)}{r.deleted_by_name ? ` · par ${r.deleted_by_name}` : ''}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ManagementUsers;
